import {
  Credenza,
  CredenzaBody,
  CredenzaTrigger,
  CredenzaContent,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaDescription,
} from '@/components/ui/credenza.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useEffect, useState } from 'react';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { UserPlantsAccessManagementDesktop } from './user-plant-access-management-desktop.tsx';
import { UserPlantAccessManagementMobile } from './user-plant-access-management-mobile.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ListRestart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserPlantsQuery } from '@/features/users/hooks/queries/[user]/list-plants.ts';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { TUser } from '@/features/users/schemas/users.schema.ts';
import { useAssignPlantsToUser } from '@/features/users/hooks/mutations/[user]/assign-plants-to-user.ts';

export type TId = string | number;

export type TPlantWithAccessControl = TPlant & {
  columnId: TId;
  newlyAdded: boolean;
};

export const UserPlantsAccessManagementDialog = ({
  user,
  children,
}: {
  user: TUser;
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width:640px)');
  const [isUserPlantsManagementOpen, setIsUserPlantsManagementOpen] = useState(false);
  const [plants, setPlants] = useState<TPlantWithAccessControl[]>([]);

  const updateUserPlantsQueryMutation = useAssignPlantsToUser({
    queryClient,
    userId: user.userId,
    onSuccess: () => {
      setIsUserPlantsManagementOpen(false);
    },
  });

  const { data: availablePlants, isLoading: isAvailablePlantsLoading } = useGetPlantsQuery({
    enabled: isUserPlantsManagementOpen,
  });

  const {
    data: assignedUserPlants,
    isFetching,
    isLoading: isUserPlantsLoading,
  } = useGetUserPlantsQuery({
    userId: user.userId,
    enabled: Boolean(user.userId) && isUserPlantsManagementOpen,
  });

  const isLoading = isAvailablePlantsLoading || isUserPlantsLoading;

  useEffect(() => {
    const initialUserPlantsList =
      availablePlants?.map((u) => {
        const columnId = assignedUserPlants?.plantIds?.includes(u.plantId)
          ? 'associated-plants'
          : 'available-plants';

        return {
          ...u,
          columnId,
          newlyAdded: false,
        };
      }) ?? [];

    setPlants(initialUserPlantsList);
  }, [isLoading, isFetching]);

  useEffect(() => {
    // reset user list

    if (!isUserPlantsManagementOpen) {
      const initialUserPlantsList =
        availablePlants?.map((u) => {
          const columnId = assignedUserPlants?.plantIds?.includes(u.plantId)
            ? 'associated-plants'
            : 'available-plants';

          return {
            ...u,
            columnId,
            newlyAdded: false,
          };
        }) ?? [];
      setPlants(initialUserPlantsList);
    }
  }, [isUserPlantsManagementOpen]);

  return (
    <Credenza open={isUserPlantsManagementOpen} onOpenChange={setIsUserPlantsManagementOpen}>
      <CredenzaTrigger asChild>{children}</CredenzaTrigger>

      <CredenzaContent className="max-w-4xl">
        <CredenzaHeader>
          <CredenzaTitle>
            Plant Access for {user.firstName} {user.lastName}
          </CredenzaTitle>
        </CredenzaHeader>

        <CredenzaBody className="sm:h-[500px] sm:px-0">
          <CredenzaDescription>Assign and manage user plants permissions.</CredenzaDescription>
          {!isMobile ? (
            <div className="mt-4 h-full">
              <UserPlantsAccessManagementDesktop
                plants={plants}
                setPlants={setPlants}
                isLoading={isLoading}
              />
            </div>
          ) : (
            <div className="mt-4 h-full">
              <UserPlantAccessManagementMobile
                plants={plants}
                setPlants={setPlants}
                isLoading={isLoading}
              />
            </div>
          )}
        </CredenzaBody>
        <CredenzaFooter className="sm:h-9">
          {isLoading && !isMobile && <Skeleton className="h-9 w-[62px]" />}
          {isLoading && isMobile && <Skeleton className="h-9"></Skeleton>}

          {!isLoading && (
            <div className="flex gap-2">
              {isMobile ? (
                <Button
                  variant="secondary"
                  disabled={updateUserPlantsQueryMutation.isPending}
                  onClick={() => {
                    const initialUserPlantsList =
                      availablePlants?.map((u) => {
                        const columnId = assignedUserPlants?.plantIds?.includes(u.plantId)
                          ? 'associated-plants'
                          : 'available-plants';

                        return {
                          ...u,
                          columnId,
                          newlyAdded: false,
                        };
                      }) ?? [];
                    setPlants(initialUserPlantsList);
                  }}
                  className="p-1 px-1.5"
                >
                  <ListRestart className="h-6 w-6" />
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        disabled={updateUserPlantsQueryMutation.isPending}
                        onClick={() => {
                          const initialUserPlantsList =
                            availablePlants?.map((u) => {
                              const columnId = assignedUserPlants?.plantIds?.includes(
                                u.plantId
                              )
                                ? 'associated-plants'
                                : 'available-plants';

                              return {
                                ...u,
                                columnId,
                                newlyAdded: false,
                              };
                            }) ?? [];
                          setPlants(initialUserPlantsList);
                        }}
                        className="p-1 px-1.5"
                      >
                        <ListRestart className="h-6 w-6" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-50 text-base text-black shadow-xs">
                      <p>Reset plant access list</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <Button
                disabled={updateUserPlantsQueryMutation.isPending}
                onClick={() => {
                  updateUserPlantsQueryMutation.mutate({
                    userId: user.userId,
                    plantIds: plants
                      .filter((p) => p.columnId === 'associated-plants')
                      .map((p) => p.plantId),
                  });
                }}
                className="flex w-full min-w-20 justify-center gap-1 text-center"
              >
                {updateUserPlantsQueryMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px] select-none">Save</span>
              </Button>
            </div>
          )}
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
